/**
 * Delete an AI Service (task 863).
 *
 * The one delete on the platform that removes a public address, so it is the
 * one that earns the type-to-confirm speed bump. Everything the dialog promises
 * is what the service actually does (tasks 861, 862): the path is freed by a
 * partial unique index on live rows, the saved tests and generated interfaces
 * are tombstoned in the same transaction as the service, the shared building
 * blocks are untouched, and a ledger row makes the whole operation reversible
 * from the Archive.
 *
 * **Why the typed text is the PATH, not the display name.** The report's wording
 * is "type the service name", and in its example the name and the path are the
 * same string. They are not the same field: `endpointDisplayName` falls back to
 * the DESCRIPTION when a service has no name, and asking a User to retype a
 * sentence is a speed bump that teaches them to paste. The path is short, unique
 * within the account, always present — and it is the thing the dialog has just
 * told them becomes free to reuse, so it is what the confirmation should be
 * about. The label names it explicitly, so nothing is left to guess.
 *
 * A service has no upstream dependents — nothing points at a service — so there
 * is no dependents read here and no `409 IN_USE` refusal to render. Both belong
 * to the entities that CAN be depended on (task 864).
 */
import { useMemo } from 'react';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { useEndpointTests } from '../../hooks/useEndpointTests';
import { useGeneratedUis } from '../../hooks/useGeneratedUis';
import { useCollaborationConfig } from '../../hooks/useCollaborationConfigs';
import { endpointDisplayName } from '../../utils/endpointLabel';
import { normalizeServicePath } from './detail/serviceSnippets';
import type { Endpoint } from '../../types';

export interface DeleteServiceDialogProps {
  endpoint: Endpoint;
  /** Close without deleting. */
  onClose: () => void;
  /** Run the delete. The caller owns the mutation and what happens after it. */
  onConfirm: () => void;
  isDeleting?: boolean;
}

/** "3 saved tests and 1 generated interface", or null when it owns neither. */
function ownedChildrenSentence(testCount: number, interfaceCount: number): string | null {
  const parts: string[] = [];
  if (testCount > 0) {
    parts.push(`${testCount} scenario${testCount === 1 ? '' : 's'}`);
  }
  if (interfaceCount > 0) {
    parts.push(`${interfaceCount} generated interface${interfaceCount === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) return null;
  return `Its ${parts.join(' and ')} go with it.`;
}

export function DeleteServiceDialog({
  endpoint,
  onClose,
  onConfirm,
  isDeleting = false,
}: DeleteServiceDialogProps) {
  // Mounted only while the dialog is open (the callers render it
  // conditionally), so these three reads happen on demand rather than on every
  // list row. Each degrades to "nothing" rather than a wrong number: a count
  // the dialog cannot prove is a count it must not state.
  const { data: tests } = useEndpointTests(endpoint.id);
  const { data: generatedUis } = useGeneratedUis(endpoint.id);
  const { data: config } = useCollaborationConfig(endpoint.collaborationConfigId ?? undefined);

  const testCount = tests?.pagination?.total ?? tests?.data?.length ?? 0;
  const interfaceCount = generatedUis?.data?.length ?? 0;

  const title = endpointDisplayName(endpoint);
  const confirmPath = (endpoint.path ?? '').replace(/^\/+/, '').replace(/\/+$/, '');

  const children = useMemo(
    () => ownedChildrenSentence(testCount, interfaceCount),
    [testCount, interfaceCount]
  );

  const message = (
    <div className="space-y-3" data-testid="delete-service-dialog-body">
      <p data-testid="delete-service-address">
        Its URL stops answering immediately, and the path{' '}
        <span className="font-mono font-semibold">{normalizeServicePath(endpoint)}</span> is free to
        reuse.
      </p>

      {children ? (
        <p data-testid="delete-service-children">{children}</p>
      ) : (
        <p data-testid="delete-service-children">
          It has no scenarios or generated interfaces to take with it.
        </p>
      )}

      <p data-testid="delete-service-stays">
        {config?.name ? (
          <>
            The collaboration config{' '}
            <span className="font-semibold">{config.name}</span> and the agents it uses stay, because
            other AI Services may use them.
          </>
        ) : (
          <>
            Its collaboration config and the agents it uses stay, because other AI Services may use
            them.
          </>
        )}
      </p>

      {/* Plain text, not a link: the Archive is one click away from a dialog the
          User is in the middle of, and the toast that follows the delete is
          where the link belongs (task 865). */}
      <p data-testid="delete-service-archive">
        You can restore it from Settings → Archive at any time.
      </p>
    </div>
  );

  return (
    <ConfirmDialog
      isOpen
      onClose={onClose}
      onConfirm={onConfirm}
      isLoading={isDeleting}
      confirmStyle="danger"
      confirmLabel="Delete AI Service"
      title={`Delete “${title}”?`}
      message={message}
      confirmText={confirmPath}
    />
  );
}

export default DeleteServiceDialog;
